import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import patientService from '../../../../services/patient.service';
import { showError, showSuccess } from '../../../../utils/ecxSwal';

const MAX_SMS_LENGTH = 640;

const PLACEHOLDER_HINT = '{PATIENT_NAME}, {PATIENT_FIRST_NAME}, {PATIENT_LAST_NAME}, {CLINIC_NAME}, {CLINIC_PHONE}, {CLINIC_ADDRESS}, {CLINIC_EMAIL}';

function SendPatientSmsModal({ show, onClose, clinicId, patient }) {
    const [previewMessage, setPreviewMessage] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            content: '',
        },
    });

    const contentValue = watch('content') || '';
    const charCount = contentValue.length;

    const patientLabel = useMemo(() => {
        if (!patient) {
            return '';
        }

        const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
        return patient.preferredName ? `${name} (${patient.preferredName})` : name;
    }, [patient]);

    const displayPhone = useMemo(() => {
        if (!patient?.cellNumber) {
            return '';
        }

        const digits = patient.cellNumber.replace(/\D/g, '');
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }

        return patient.cellNumber;
    }, [patient]);

    useEffect(() => {
        if (show && patient) {
            reset({ content: '' });
            setPreviewMessage('');
            setShowPreview(false);
        }
    }, [show, patient, reset]);

    const buildPayload = (data) => ({
        content: data.content?.trim(),
    });

    const handlePreview = async (data) => {
        if (!clinicId || !patient?.id) {
            return;
        }

        setPreviewLoading(true);
        setShowPreview(true);

        try {
            const res = await patientService.previewPatientSms(clinicId, patient.id, buildPayload(data));
            setPreviewMessage(res.data.message || '');
        } catch (error) {
            console.error('Failed to preview patient SMS', error);
            showError({
                title: 'Preview Failed',
                text: error?.response?.data?.errorMessage || 'Could not generate the SMS preview.',
            });
            setShowPreview(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const onSend = async (data) => {
        if (!clinicId || !patient?.id) {
            return;
        }

        setSending(true);

        try {
            await patientService.sendPatientSms(clinicId, patient.id, buildPayload(data));
            showSuccess({
                title: 'SMS Queued',
                text: `SMS to ${displayPhone} has been queued for delivery.`,
            });
            onClose();
        } catch (error) {
            console.error('Failed to send patient SMS', error);
            showError({
                title: 'Send Failed',
                text: error?.response?.data?.errorMessage || 'Could not queue the SMS for sending.',
            });
        } finally {
            setSending(false);
        }
    };

    if (!patient) {
        return null;
    }

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="lg"
            scrollable
            backdrop="static"
            className="patient-send-sms-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-chat-dots me-2" />
                    Send SMS
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit(onSend)}>
                <Modal.Body>
                    <div className="patient-send-sms-modal__meta mb-3">
                        <div className="patient-send-sms-modal__meta-item">
                            <span className="patient-send-sms-modal__meta-label">Patient</span>
                            <span className="patient-send-sms-modal__meta-value">{patientLabel}</span>
                        </div>
                        <div className="patient-send-sms-modal__meta-item">
                            <span className="patient-send-sms-modal__meta-label">To</span>
                            <span className="patient-send-sms-modal__meta-value">{displayPhone}</span>
                        </div>
                    </div>

                    <Form.Group className="mb-3" controlId="patientSmsContent">
                        <Form.Label>Message</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            placeholder="Write the SMS message for this patient"
                            maxLength={MAX_SMS_LENGTH}
                            isInvalid={!!errors.content}
                            {...register('content', {
                                required: 'Message is required',
                                maxLength: {
                                    value: MAX_SMS_LENGTH,
                                    message: `Message must be ${MAX_SMS_LENGTH} characters or fewer`,
                                },
                            })}
                        />
                        <div className="d-flex justify-content-between align-items-start mt-1">
                            <Form.Text className="text-muted">
                                Placeholders: {PLACEHOLDER_HINT}
                            </Form.Text>
                            <Form.Text className={charCount >= MAX_SMS_LENGTH ? 'text-danger' : 'text-muted'}>
                                {charCount}/{MAX_SMS_LENGTH}
                            </Form.Text>
                        </div>
                        <Form.Control.Feedback type="invalid">
                            {errors.content?.message}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {showPreview && (
                        <div className="patient-send-sms-modal__preview">
                            <h6 className="mb-3 fw-normal">Preview</h6>
                            <div className="patient-send-sms-modal__phone">
                                <div className="patient-send-sms-modal__phone-notch" />
                                <div className="patient-send-sms-modal__phone-screen">
                                    <div className="patient-send-sms-modal__phone-header">
                                        <span className="patient-send-sms-modal__phone-contact">{patientLabel}</span>
                                        <span className="patient-send-sms-modal__phone-number">{displayPhone}</span>
                                    </div>
                                    <div className="patient-send-sms-modal__phone-body">
                                        {previewLoading ? (
                                            <div className="patient-send-sms-modal__preview-loading">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Generating preview...
                                            </div>
                                        ) : previewMessage ? (
                                            <div className="patient-send-sms-modal__message-bubble">
                                                {previewMessage}
                                            </div>
                                        ) : (
                                            <p className="text-muted mb-0 small">No preview available.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose} disabled={sending || previewLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="outline-primary"
                        type="button"
                        disabled={sending || previewLoading || !contentValue.trim()}
                        onClick={handleSubmit(handlePreview)}
                    >
                        {previewLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Previewing...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-eye me-1" />
                                Preview
                            </>
                        )}
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={sending || previewLoading || !contentValue.trim()}
                    >
                        {sending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-send me-1" />
                                Send SMS
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default SendPatientSmsModal;
